import React, { ReactNode, useContext, useEffect, useState } from 'react';
import { notification } from 'antd';

import SavingsTypeDialog from '@totem/components/administration/optimize/savingsTypes/savingsTypeDialog';
import SavingsTypesContext from '@totem/components/administration/optimize/savingsTypes/savingsTypesContext';
import {
  SavingsType,
  SavingsTypesDocument,
} from '@totem/components/administration/optimize/savingsTypes/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { TICKETS_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';

const SAVINGS_TYPES_ENDPOINT = `${TICKETS_ENDPOINT}/optimize/savingsTypes`;

// null = closed, -1 = adding, otherwise the index being edited.
type DialogState = number | null;

type Props = {
  children?: ReactNode;
};

const SavingsTypesContainer = ({ children }: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [refreshData, setRefreshData] = useState<boolean>(true);
  const [document, setDocument] = useState<SavingsTypesDocument | null>(null);
  const [dialog, setDialog] = useState<DialogState>(null);

  useEffect(() => {
    if (!refreshData) {
      return;
    }
    setRefreshData(false);

    setLoading(true);

    fetch(SAVINGS_TYPES_ENDPOINT, {
      method: 'GET',
      headers: new Headers({ Authorization: `Bearer ${getApiToken()}` }),
    })
      .then((res) => (CheckResponseShowError(res) ? res.json() : null))
      .then((result: SavingsTypesDocument | null) => {
        if (result !== null) {
          setDocument(result);
        }
      })
      .finally(() => setLoading(false));
  }, [refreshData, getApiToken]);

  const onSave = (data: SavingsType[]): Promise<boolean> => {
    if (document === null) {
      return Promise.resolve(false);
    }
    setSaving(true);

    return fetch(SAVINGS_TYPES_ENDPOINT, {
      method: 'PUT',
      headers: new Headers({ Authorization: `Bearer ${getApiToken()}` }),
      body: JSON.stringify({ data, updatedAt: document.updatedAt ?? null }),
    })
      .then((res) => {
        // Someone else saved in between: show their list so the change can be redone on top of it.
        if (res.status === 409) {
          setRefreshData(true);
        }
        return CheckResponseShowError(res) ? res.json() : null;
      })
      .then((result: SavingsTypesDocument | null) => {
        if (result === null) {
          return false;
        }
        setDocument(result);

        notification.success({ message: 'Savings types saved', duration: 3 });
        return true;
      })
      .catch(() => false)
      .finally(() => setSaving(false));
  };

  return (
    <SavingsTypesContext.Provider
      value={{
        loading,
        saving,
        document,
        onSave,
        onRefresh: () => setRefreshData(true),
        onAdd: () => setDialog(-1),
        onEdit: (index: number) => setDialog(index),
      }}
    >
      {children}
      {dialog !== null && document !== null && (
        <SavingsTypeDialog
          index={dialog}
          savingsTypes={document.data}
          saving={saving}
          onClose={() => setDialog(null)}
          onSave={onSave}
        />
      )}
    </SavingsTypesContext.Provider>
  );
};

export default SavingsTypesContainer;
